<template>
  <div>
    <div v-if="displayContent.type === 'text'" class="flex flex-col gap-2">
      <template v-if="translationEnabled">
        <div class="space-y-1">
          <p
            :class="[
              'text-[10px] font-semibold',
              isSent ? 'text-white' : 'text-[var(--p-primary-color)]',
            ]"
          >
            {{ $t('whatsapp.original_message') }}
          </p>
          <TextMessageContent
            :text="displayContent.content || displayContent.text || ''"
            :is-sent="isSent"
          />
        </div>

        <div
          v-if="
            translation?.status === 'ready' || translation?.status === 'error' || translationLoading
          "
          class="space-y-1 border-t border-current/15 pt-2"
        >
          <p class="text-[10px] font-semibold text-[var(--p-surface-0)]">
            {{ $t('whatsapp.translated_message') }}
          </p>
          <TextMessageContent
            v-if="displayedTranslation"
            :text="displayedTranslation"
            :is-sent="isSent"
          />
          <button
            v-else-if="translation?.status === 'error'"
            type="button"
            class="text-left text-xs font-medium text-red-500 hover:text-red-600 dark:text-red-300 dark:hover:text-red-200"
            @click="$emit('retry-translation')"
          >
            {{ $t('whatsapp.retry_translation') }}
          </button>
          <TypingIndicator v-else-if="translationLoading" />
        </div>
      </template>

      <TextMessageContent
        v-else
        :text="displayContent.content || displayContent.text || ''"
        :is-sent="isSent"
      />
    </div>

    <AudioMessageContent
      v-else-if="displayContent.type === 'audio'"
      :url="displayContent.url ?? undefined"
      :file-name="displayContent.file_name ?? undefined"
      :duration="displayContent.duration ?? undefined"
      :is-sent="isSent"
    />

    <ImageMessageContent
      v-else-if="displayContent.type === 'image'"
      :url="displayContent.url ?? undefined"
      :message-id="messageId"
      :file-name="displayContent.file_name ?? undefined"
      :caption="displayContent.caption ?? undefined"
      :is-sent="isSent"
    />

    <VideoMessageContent
      v-else-if="displayContent.type === 'video'"
      :url="displayContent.url ?? undefined"
      :message-id="messageId"
      :caption="displayContent.caption ?? undefined"
      :is-sent="isSent"
    />

    <DocumentMessageContent
      v-else-if="displayContent.type === 'document' || displayContent.type === 'file'"
      :url="displayContent.url ?? undefined"
      :file-name="displayContent.file_name ?? undefined"
      :file-size="displayContent.file_size ?? undefined"
      :caption="displayContent.caption ?? undefined"
      :is-sent="isSent"
    />

    <InteractionMessageContent
      v-else-if="displayContent.type === 'interaction'"
      :content="formatInteractionContent(displayContent.content || '')"
    />

    <StickerMessageContent
      v-else-if="displayContent.type === 'sticker'"
      :url="displayContent.url ?? undefined"
      :content-type="displayContent.content_type ?? undefined"
      :is-sent="isSent"
    />

    <LocationMessageContent
      v-else-if="displayContent.type === 'location'"
      :longitude="String(displayContent.longitude ?? '')"
      :latitude="String(displayContent.latitude ?? '')"
      :name="displayContent.name ?? undefined"
      :address="displayContent.address ?? undefined"
      :is-sent="isSent"
    />

    <ContactMessageContent
      v-else-if="displayContent.type === 'contact'"
      :contacts="displayContent.contacts || []"
      :is-sent="isSent"
    />

    <TemplateMessageContent
      v-else-if="displayContent.type === 'template'"
      :content="displayContent.content || ''"
      :is-sent="isSent"
    />

    <QuickReplyMessageContent
      v-else-if="
        displayContent.type === 'quick_reply' ||
        displayContent.type === 'list_reply' ||
        displayContent.type === 'button_reply'
      "
      :text="displayContent.content || ''"
      :is-sent="isSent"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import TextMessageContent from '../../molecules/message-content/TextMessageContent.vue'
import AudioMessageContent from '../../molecules/message-content/AudioMessageContent.vue'
import ImageMessageContent from '../../molecules/message-content/ImageMessageContent.vue'
import VideoMessageContent from '../../molecules/message-content/VideoMessageContent.vue'
import DocumentMessageContent from '../../molecules/message-content/DocumentMessageContent.vue'
import InteractionMessageContent from '../../molecules/message-content/InteractionMessageContent.vue'
import StickerMessageContent from '../../molecules/message-content/StickerMessageContent.vue'
import LocationMessageContent from '../../molecules/message-content/LocationMessageContent.vue'
import ContactMessageContent from '../../molecules/message-content/ContactMessageContent.vue'
import TemplateMessageContent from '../../molecules/message-content/TemplateMessageContent.vue'
import QuickReplyMessageContent from '../../molecules/message-content/QuickReplyMessageContent.vue'
import TypingIndicator from '@/components/atoms/typing/TypingIndicator.vue'
import type { MessageContent as ServiceMessageContent } from '../../types/interfaces'
import { MessageType, type MessageSourceType } from '../../types/enums'
import { parseUtcDate } from '../../composables/useTimeFormatter'

interface Props {
  content: ServiceMessageContent
  sourceType: MessageSourceType
  messageId?: string
  translationEnabled?: boolean
  translationLanguage?: string
  translationLoading?: boolean
}

const props = defineProps<Props>()
defineEmits<{
  'retry-translation': []
}>()

const isSent = computed(() => props.sourceType === 'SALIENTE' || props.sourceType === 'TEMPLATE')
const translation = computed(
  () => props.content.ai_translate?.[props.translationLanguage || 'en'] ?? null,
)
const displayedTranslation = computed(() =>
  translation.value?.status === 'ready' ? translation.value.translated_content : '',
)

const URL_REGEX = /(https?:\/\/[^\s]+)/i
const TRAILING_ISO_DATE_REGEX =
  /\s+(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+\-]\d{2}:?\d{2}))$/
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg']
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v', '.avi', '.mpeg', '.mpg', '.3gp', '.3gpp']

const extractUrl = (value?: string | null) => value?.match(URL_REGEX)?.[0] ?? null

const getUrlPath = (url: string) => {
  try {
    return new URL(url).pathname.toLowerCase()
  } catch {
    return url.split('?')[0]?.toLowerCase() ?? url.toLowerCase()
  }
}

const hasExtension = (url: string, extensions: string[]) => {
  const path = getUrlPath(url)
  return extensions.some((extension) => path.endsWith(extension))
}
const formatInteractionContent = (content: string) => {
  const match = content.match(TRAILING_ISO_DATE_REGEX)
  const timestamp = match?.[1]

  if (!timestamp || match.index === undefined) return content

  const date = parseUtcDate(timestamp)
  if (Number.isNaN(date.getTime())) return content

  const message = content.slice(0, match.index).trimEnd()
  const dateLabel = date.toLocaleDateString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const timeLabel = date.toLocaleTimeString('es-CO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })

  return `${message} ${dateLabel} a las ${timeLabel}`
}

const isMediaTypeFallbackText = (value: string, mediaType: MessageType) =>
  value.trim().toLowerCase() === mediaType

const displayContent = computed<ServiceMessageContent>(() => {
  const textContent = props.content.content || props.content.text || ''
  const url = props.content.url || extractUrl(textContent)
  const contentType = (
    props.content.content_type ||
    props.content.file_content_type ||
    ''
  ).toLowerCase()

  if (!url) return props.content

  const isImage =
    props.content.type === MessageType.IMAGE ||
    contentType.startsWith('image/') ||
    hasExtension(url, IMAGE_EXTENSIONS)
  const isVideo =
    props.content.type === MessageType.VIDEO ||
    contentType.startsWith('video/') ||
    hasExtension(url, VIDEO_EXTENSIONS)

  if (!isImage && !isVideo) return props.content

  const trimmedTextContent = textContent.trim()
  const isFileNameFallbackCaption = Boolean(
    isSent.value &&
      isImage &&
      props.content.file_name &&
      (trimmedTextContent === props.content.file_name ||
        trimmedTextContent.startsWith(`${props.content.file_name} (`)),
  )
  const mediaType = isImage ? MessageType.IMAGE : MessageType.VIDEO
  const rawCaption = props.content.caption || null
  const caption =
    rawCaption && !isMediaTypeFallbackText(rawCaption, mediaType)
      ? rawCaption
      : !trimmedTextContent ||
          trimmedTextContent === url ||
          isFileNameFallbackCaption ||
          isMediaTypeFallbackText(trimmedTextContent, mediaType)
        ? null
        : textContent

  return {
    ...props.content,
    type: mediaType,
    url,
    caption,
  }
})
</script>
